import type { SearchSelectRef } from '@talos/kyoko';
import { type Customer, FormGroup, HStack, type MarketAccount, SearchSelect } from '@talos/kyoko';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface CustomerSelectorProps {
  customers: Customer[];
  customerMarketAccountsList: MarketAccount[];
  marketAccount: MarketAccount | undefined;
  onChange: (marketAccount: MarketAccount | undefined) => unknown;
}

export function CustomerSelector({
  customers,
  customerMarketAccountsList,
  marketAccount,
  onChange: onChangeMarketAccount,
}: CustomerSelectorProps) {
  const [customer, setCustomer] = useState<Customer>();
  const marketAccountList = useMemo(() => {
    if (!customer) {
      return [];
    }

    return customerMarketAccountsList.filter(marketAccount => marketAccount.Counterparty === customer.Name);
  }, [customerMarketAccountsList, customer]);

  useEffect(() => {
    if (!marketAccount || customer?.Name === marketAccount.Counterparty) {
      return;
    }

    setCustomer(customers.find(customer => customer.Name === marketAccount.Counterparty));
  }, [customers, customer, setCustomer, marketAccount]);

  const mktAcntSelectRef = useRef<SearchSelectRef>(null);
  const onChangeCustomer = useCallback(
    (selected: Customer | undefined) => {
      if (selected?.Name !== customer?.Name) {
        onChangeMarketAccount(undefined);
      }

      setCustomer(selected);
      mktAcntSelectRef.current?.openMenu();
    },
    [customer, setCustomer, onChangeMarketAccount]
  );
  const getCustomerLabel = useCallback(({ DisplayName, Name }: Customer) => DisplayName ?? Name, []);
  const getMarketAccountLabel = useCallback(
    ({ DisplayName, SourceAccountID }: MarketAccount) => DisplayName ?? SourceAccountID,
    []
  );

  return (
    <HStack gap="spacing16" mb="spacing16" alignItems="center">
      <FormGroup label="Customer" flex={1} mb={0}>
        <SearchSelect
          selection={customer}
          options={customers}
          onChange={selected => onChangeCustomer(selected)}
          getLabel={getCustomerLabel}
        />
      </FormGroup>
      <FormGroup label="Market Account" flex={1} mb={0}>
        <SearchSelect
          selection={marketAccount}
          options={marketAccountList}
          disabled={isEmpty(marketAccountList)}
          onChange={selected => onChangeMarketAccount(selected)}
          getLabel={getMarketAccountLabel}
          ref={mktAcntSelectRef}
        />
      </FormGroup>
    </HStack>
  );
}
